import React, { useState, useEffect } from 'react';
import { checkAuth, authenticatedRequest } from '../utils/auth';
import EditRestaurantModal from './EditRestaurantModal';
import './RestaurantsList.css';
import './EditRestaurantModal.css';

const RestaurantsList = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingRestaurant, setEditingRestaurant] = useState(null);

  useEffect(() => {
    const initializeComponent = async () => {
      const isAuthenticated = await checkAuth();
      if (!isAuthenticated) {
        return;
      }
      
      await fetchRestaurants();
    };
    
    initializeComponent();
  }, []);

  const fetchRestaurants = async () => {
    try {
      setLoading(true);
      const response = await authenticatedRequest('GET', '/restaurants');

      if (response.data.ok) {
        setRestaurants(response.data.restaurants);
      } else {
          

        setError(response.data.message || 'خطا در دریافت لیست رستوران‌ها');
      }
    } catch (err) {
     
      setError('خطا در اتصال به سرور');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRestaurant = async (restaurantId) => {
    if (!window.confirm('آیا از حذف این رستوران اطمینان دارید؟')) {
      return;
    }

    try {
      const response = await authenticatedRequest('DELETE', `/restaurants/${restaurantId}`);
      
      if (response.data.ok) {
        setRestaurants(restaurants.filter(restaurant => restaurant._id !== restaurantId));
        setError('');
      } else {
        setError(response.data.message || 'خطا در حذف رستوران');
      }
    } catch (err) {
      console.error('Error deleting restaurant:', err);
      setError('خطا در حذف رستوران');
    }
  };

  const handleUpdateRestaurant = async (id, formData) => {
    try {
      const response = await authenticatedRequest('PUT', `/restaurants/${id}`, formData, true);
      
      if (response.data.ok) {
        setRestaurants(restaurants.map(restaurant => 
          restaurant._id === id ? response.data.restaurant : restaurant
        ));
        setError('');
        return true;
      } else {
        setError(response.data.message || 'خطا در به‌روزرسانی رستوران');
        return false;
      }
    } catch (err) {
      console.error('Error updating restaurant:', err);
      setError('خطا در اتصال به سرور');
      return false;
    }
  };

  const handleEditClick = (restaurant) => {
    setEditingRestaurant(restaurant);
  };

  if (loading) {
    return (
      <div className="restaurants-list-container">
        <div className="loading">در حال بارگذاری...</div>
      </div>
    );
  }

  // Helper function to get category name in Persian
  const getCategoryName = (category) => {
    const categories = {
      'classic': 'کلاسیک',
      'fastfood': 'فست فود',
      'cafe': 'کافه',
      'vitamin': 'ویتامین',
      'foreign': 'خارجی',
      'vegetarian': 'گیاهی',
      'seafood': 'غذای دریایی'
    };
    return categories[category] || category;
  };

  // Format active hours for display
  const formatActiveHours = (activeHours) => {
    if (!activeHours) return 'ثبت نشده';
    
    const days = {
      saturday: 'شنبه',
      sunday: 'یکشنبه',
      monday: 'دوشنبه',
      tuesday: 'سه‌شنبه',
      wednesday: 'چهارشنبه',
      thursday: 'پنجشنبه',
      friday: 'جمعه'
    };
    
    return Object.entries(activeHours)
      .map(([day, hours]) => {
        if (!hours || !hours.from || !hours.to) return null;
        return `${days[day] || day}: ${hours.from} - ${hours.to}`;
      })
      .filter(Boolean)
      .join(' | ');
  };

  const handleBack = () => {
    window.history.back();
  };

  return (
    <div className="restaurants-list-container">
      <div className="restaurants-list-card">
        <div className="restaurants-list-header">
          <div className="header-actions">
            <button onClick={handleBack} className="back-button">
              <span className="back-arrow">←</span> بازگشت
            </button>
          </div>
          <h2>لیست رستوران‌ها</h2>
          <p>مدیریت رستوران‌های ثبت شده در سیستم</p>
        </div>

        {loading ? (
          <div className="loading">در حال بارگذاری رستوران‌ها...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : restaurants.length === 0 ? (
          <div className="no-restaurants">هیچ رستورانی یافت نشد</div>
        ) : (
          <div className="restaurants-grid">
            {restaurants.map(restaurant => (
              <div key={restaurant._id} className="restaurant-card">
                <div className="restaurant-header">
                  <h3>{restaurant.name}</h3>
                  <span className={`restaurant-category ${restaurant.category}`}>
                    {getCategoryName(restaurant.category)}
                  </span>
                </div>
                
                <div className="restaurant-content">
                  <p className="restaurant-description">{restaurant.shortDescription || 'بدون توضیحات'}</p>
                  
                  <div className="restaurant-details">
                    <div className="detail-item">
                      <strong>شهر:</strong> {restaurant.city || 'ثبت نشده'}
                    </div>
                    <div className="detail-item">
                      <strong>آدرس:</strong> {restaurant.address || 'ثبت نشده'}
                    </div>
                    <div className="detail-item">
                      <strong>تلفن:</strong> {Array.isArray(restaurant.phone) ? restaurant.phone.join(' - ') : restaurant.phone || 'ثبت نشده'}
                    </div>
                    {restaurant.activeHours && (
                      <div className="detail-item">
                        <strong>ساعات کاری:</strong> {formatActiveHours(restaurant.activeHours)}
                      </div>
                    )}
                  </div>
                  
                  {(restaurant.logo || (restaurant.images && restaurant.images.length > 0)) && (
                    <div className="restaurant-images">
                      <h4>تصاویر:</h4>
                      <div className="images-grid">
                        {restaurant.images.slice(0, 3).map((image, index) => (
                          <img 
                            key={index} 
                            src={image.startsWith('http') ? image : `https://apipublic.menuoff.com/${image}`} 
                            alt={`${restaurant.name} ${index + 1}`} 
                          />
                        ))}
                        {restaurant.images.length > 3 && (
                          <div className="more-images">
                            +{restaurant.images.length - 3} تصویر دیگر
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="restaurant-actions">
                  <button
                    onClick={() => handleEditClick(restaurant)}
                    className="edit-btn"
                  >
                    ویرایش
                  </button>
                  <button
                    onClick={() => handleDeleteRestaurant(restaurant._id)}
                    className="delete-btn"
                  >
                    حذف
                  </button>
                </div>

                <div className="restaurant-badges">
                  {restaurant.isActive && (
                    <span className="badge active">فعال</span>
                  )}
                  {restaurant.isOpen && (
                    <span className="badge open">باز</span>
                  )}
                  {restaurant.hasCafe && (
                    <span className="badge cafe">کافه</span>
                  )}
                  {restaurant.innerDelivery && (
                    <span className="badge delivery">تحویل داخلی</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editingRestaurant && (
        <EditRestaurantModal
          restaurant={editingRestaurant}
          onClose={() => setEditingRestaurant(null)}
          onUpdate={handleUpdateRestaurant}
        />
      )}
    </div>
  );
};

export default RestaurantsList;
